import { deleteDpopKeyPair } from 'utils/dpopKeyStore'
import { BUILD_STORAGE_KEY, reseedBuildHash } from './localStorage'

/**
 * Keys that must remain after logout (SIM-377). They are not session credentials.
 *
 * - clarity_consent: machine-level analytics choice. Clearing it re-prompts every
 *   user (including the next person on this device) for Microsoft Clarity.
 * - dismissed_overlaps_*: FE-only social-overlap dismissals, scoped by profile id.
 *   Clearing them would re-show notifications the user already dismissed.
 * - re_engagement_banner_dismissed_*: same for the re-engagement banner.
 *
 * Everything else is treated as user data and is removed, including:
 * - `state` (persisted Redux: tokens, SSO/impersonation tokens, DPoP flag, profiles)
 * - `build` (re-seeded immediately so loadState keeps working)
 * - `spiderGraphs` (team graph history)
 * - `addCardForRenewal` / `simpli5-chunk-reload-count` (sessionStorage)
 */
export const LOGOUT_SURVIVING_LOCAL_STORAGE_KEYS = Object.freeze(['clarity_consent'])

export const LOGOUT_SURVIVING_LOCAL_STORAGE_PREFIXES = Object.freeze([
  'dismissed_overlaps_',
  're_engagement_banner_dismissed_',
])

export const shouldSurviveLogout = key => {
  if (!key || key === BUILD_STORAGE_KEY) {
    return false
  }

  if (LOGOUT_SURVIVING_LOCAL_STORAGE_KEYS.includes(key)) {
    return true
  }

  return LOGOUT_SURVIVING_LOCAL_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))
}

const snapshotStorageKeys = storage => {
  const keys = []
  for (let index = 0; index < storage.length; index += 1) {
    keys.push(storage.key(index))
  }
  return keys
}

export const clearLocalStorageOnLogout = () => {
  try {
    snapshotStorageKeys(localStorage).forEach(key => {
      if (key && !shouldSurviveLogout(key)) {
        localStorage.removeItem(key)
      }
    })
    reseedBuildHash()
  } catch (error) {
    try {
      reseedBuildHash()
    } catch (reseedError) {
      // Storage is unavailable; logout must still succeed.
    }
  }
}

export const clearSessionStorageOnLogout = () => {
  try {
    sessionStorage.clear()
  } catch (error) {
    // Privacy modes can throw; do not fail logout.
  }
}

const deleteIndexedDatabase = name =>
  new Promise(resolve => {
    try {
      const request = indexedDB.deleteDatabase(name)
      request.onsuccess = () => resolve()
      request.onerror = () => resolve()
      request.onblocked = () => resolve()
    } catch (error) {
      resolve()
    }
  })

export const clearIndexedDBOnLogout = async () => {
  try {
    if (typeof indexedDB === 'undefined') {
      return
    }

    if (typeof indexedDB.databases === 'function') {
      const databases = await indexedDB.databases()
      await Promise.all(
        (databases || []).map(database => {
          if (!database || !database.name) {
            return undefined
          }
          return deleteIndexedDatabase(database.name)
        }),
      )
    }
  } catch (error) {
    // Safari / privacy modes may omit databases() or throw.
  }
}

export const clearSessionTimer = () => {
  try {
    if (typeof window !== 'undefined' && window.api5D && typeof window.api5D.clearTimeout === 'function') {
      window.api5D.clearTimeout()
    }
  } catch (error) {
    // API singleton may be absent in tests.
  }
}

/**
 * Clears localStorage (except documented survivors), sessionStorage, and IndexedDB.
 * Re-seeds `build` so the next loadState() does not treat a missing hash as a mismatch.
 * Never throws.
 *
 * The DPoP key database is deleted by name first (EEDTD-101). clearIndexedDBOnLogout
 * can only find databases through indexedDB.databases(), which some Safari versions do
 * not implement, so it cannot be relied on to remove the session's signing key.
 */
export const clearBrowserStorageOnLogout = async () => {
  clearSessionTimer()
  clearLocalStorageOnLogout()
  clearSessionStorageOnLogout()
  await deleteDpopKeyPair()
  await clearIndexedDBOnLogout()
}

export default clearBrowserStorageOnLogout
